import React, { useState, useEffect } from "react";
import { useTranslation } from "../LanguageContext";
import ChartView from "./ChartView";

export default function ResultsPanel({ result }) {
  const { t } = useTranslation();
  const [view, setView] = useState("table");

  // When a new result arrives with a chart, default to chart view
  useEffect(() => {
    if (result?.chart) {
      setView("chart");
    } else {
      setView("table");
    }
  }, [result]);

  if (!result) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2 style={styles.title}>{t.results}</h2>
        </div>
        <div style={styles.empty}>
          <p style={styles.emptyText}>{t.resultsEmpty}</p>
        </div>
      </div>
    );
  }

  const { columns, rows, chart } = result;

  if (!columns.length || !rows.length) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2 style={styles.title}>{t.results}</h2>
        </div>
        <div style={styles.empty}>
          <p style={styles.emptyText}>{t.noData}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>{t.results}</h2>
        <span style={styles.count}>
          {rows.length} {rows.length !== 1 ? t.rows : t.row}
        </span>
        {chart && (
          <div style={styles.viewToggle}>
            <button
              style={{ ...styles.toggleBtn, ...(view === "chart" ? styles.toggleActive : {}) }}
              onClick={() => setView("chart")}
            >
              Chart
            </button>
            <button
              style={{ ...styles.toggleBtn, ...(view === "table" ? styles.toggleActive : {}) }}
              onClick={() => setView("table")}
            >
              Table
            </button>
          </div>
        )}
      </div>

      {view === "chart" && chart ? (
        <div style={styles.chartWrapper}>
          <ChartView chart={chart} columns={columns} rows={rows} />
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col} style={styles.th}>
                    {col.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                  {row.map((cell, j) => (
                    <td key={j} style={styles.td}>
                      {cell === null || cell === undefined ? (
                        <span style={styles.null}>—</span>
                      ) : (
                        String(cell)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #e1e1e1",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#333",
  },
  count: {
    fontSize: "0.8rem",
    color: "#888",
    background: "#f0f0f0",
    padding: "2px 8px",
    borderRadius: "10px",
  },
  viewToggle: {
    display: "flex",
    gap: "2px",
    marginLeft: "auto",
    background: "#f0f0f0",
    borderRadius: "8px",
    padding: "2px",
  },
  toggleBtn: {
    background: "none",
    border: "none",
    borderRadius: "6px",
    padding: "3px 12px",
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#888",
    cursor: "pointer",
  },
  toggleActive: {
    background: "#fff",
    color: "#c4622d",
    fontWeight: 600,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
  chartWrapper: {
    flex: 1,
    overflowY: "auto",
    background: "#fff",
  },
  empty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: "#aaa",
    fontSize: "0.9rem",
    textAlign: "center",
    padding: "0 40px",
  },
  tableWrapper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.85rem",
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f8f8f8",
    textAlign: "left",
    padding: "8px 12px",
    borderBottom: "2px solid #ddd",
    fontWeight: 600,
    color: "#444",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
  },
  td: {
    padding: "7px 12px",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
    whiteSpace: "nowrap",
  },
  trEven: {
    background: "#fff",
  },
  trOdd: {
    background: "#fafafa",
  },
  null: {
    color: "#ccc",
  },
};
